import { useState } from "react";
import { Link } from "react-router-dom";
import {
  formatDate,
  formatDateWithTime,
  orderStatusOptions,
} from "../../../shared/utility";
import TextWithLabel from "../../controls/TextWithLabel";
import ManufactureRecipeItemsTable from "../production/components/ManufactureRecipeItemsTable";
import ManufactureIngredientsTable from "../production/components/ManufactureIngredientsTable";
import TraceInventoryArea from "./TraceInventoryArea";
import PackagedProductTraceDetailFields from "./PackagedProductTraceDetailFields";
import PackagingHistoryDetailPanel from "./PackagingHistoryDetailPanel";
import ReassignCartonsPanel from "./ReassignCartonsPanel";
import SlideOver from "../../slideOver/SlideOver";
import { Accordion, AccordionTab } from "primereact/accordion";

const viewButtonClass =
  "inline-flex items-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";
const accordionClass = "traceability-accordion space-y-2";
const accordionHeaderClass =
  "rounded-lg border border-gray-200 bg-white hover:bg-gray-50";
const accordionContentClass = "rounded-b-lg border-x border-b border-gray-200 bg-white";

export default function TraceLotNumber(props) {
  const {
    product,
    openReceivingItemInformation,
    onSearchLotNumbers,
    reassignPackagingsById,
    onRefreshTrace,
  } = props;

  const [packagingSlideOver, setPackagingSlideOver] = useState({
    open: false,
    childComponent: null,
  });

  const convertStatus = (status) => {
    const statusOption = orderStatusOptions.filter((el) => el.value === status);

    return statusOption.length === 1
      ? { text: statusOption[0].label, type: statusOption[0].type }
      : { text: orderStatusOptions[0].label, type: orderStatusOptions[0].type };
  };

  const hasInventory = product.inventory != null;

  const openPackagingDetailsPanel = (singleItem) => {
    setPackagingSlideOver({
      open: true,
      childComponent: (
        <PackagingHistoryDetailPanel
          singleItem={singleItem}
          setOpen={setPackagingSlideOver}
          onOpenReassign={openReassignPanel}
        />
      ),
    });
  };

  const openReassignPanel = (singleItem) => {
    setPackagingSlideOver({
      open: true,
      childComponent: (
        <ReassignCartonsPanel
          singleItem={singleItem}
          setOpen={setPackagingSlideOver}
          onSearchLotNumbers={onSearchLotNumbers}
          reassignPackagingsById={reassignPackagingsById}
          onRefreshTrace={onRefreshTrace}
          sourceProductionRunName={product?.produced_product?.product_name}
        />
      ),
    });
  };

  return (
    <>
    <div className="flow-root overflow-x-auto">
      <div className="inline-block min-w-full py-2 align-middle">
        <div className="gap-4 mt-2">
          <Accordion className={accordionClass} multiple activeIndex={[0]}>
            <AccordionTab
              header="Production History"
              headerClassName={accordionHeaderClass}
              contentClassName={accordionContentClass}
            >
              <div className="py-2">
                <div className="overflow-hidden p-4 flex">
                  <div className="grid grid-cols-4 gap-4 mt-2 flex-grow">
                    <TextWithLabel
                      label="Product Name"
                      text={product.produced_product.product_name}
                    />
                    <TextWithLabel
                      label="PLU"
                      text={product.produced_product.plu}
                    />
                    <TextWithLabel
                      label="Description"
                      text={product.produced_product.description_en}
                    />
                    <TextWithLabel
                      label="Lot Number"
                      text={product.produced_product.lot_number}
                    />
                    <TextWithLabel
                      label="Production Date"
                      text={formatDate(
                        product.produced_product.production_date,
                      )}
                    />
                    <TextWithLabel
                      label="Supervisor"
                      text={product.produced_product.supervisor_name}
                    />
                    <TextWithLabel
                      label="Certified By"
                      text={product.produced_product.certification_name}
                    />
                    <TextWithLabel label="Claims(s)" text="" />
                    <TextWithLabel
                      label="Production Room"
                      text={product.produced_product.production_room}
                    />
                    <TextWithLabel
                      label="Production Room Temp C&deg;"
                      text={
                        product.produced_product.production_room_temperature
                      }
                    />
                    <TextWithLabel
                      label="Raw Product Temp C&deg;"
                      text={product.produced_product.raw_product_temperature}
                    />
                  </div>
                  <div className="ml-4 flex items-start">
                    <Link
                      className={viewButtonClass}
                      to={
                        "/manufactured-products/" + product.produced_product.id
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </Link>
                  </div>
                </div>
              </div>
            </AccordionTab>
            {hasInventory && (
              <AccordionTab
                header="Inventory"
                headerClassName={accordionHeaderClass}
                contentClassName={accordionContentClass}
              >
                <div className="py-2">
                  <TraceInventoryArea inventory={product.inventory} />
                </div>
              </AccordionTab>
            )}
            <AccordionTab
              header="Ingredients"
              headerClassName={accordionHeaderClass}
              contentClassName={accordionContentClass}
            >
              <div className="py-2">
                {product.produced_product.selectedRecipe ? (
                  <ManufactureRecipeItemsTable
                    selectedRecipe={product.produced_product.selectedRecipe}
                    values={product.produced_product}
                    openReceivingItemInformation={openReceivingItemInformation}
                    isTraceability={true}
                    showInStock={false}
                    showTitle={false}
                  />
                ) : (
                  <ManufactureIngredientsTable
                    productAssemblyItems={
                      product.produced_product.ingredients ?? []
                    }
                    openReceivingItemInformation={openReceivingItemInformation}
                    isTraceability={true}
                  />
                )}
              </div>
            </AccordionTab>
            <AccordionTab
              header="Packaging History"
              headerClassName={accordionHeaderClass}
              contentClassName={accordionContentClass}
            >
              <div className="py-2">
                <div className="overflow-hidden">
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {product.packaged_product?.length > 0 ? (
                      product.packaged_product.map((singleItem) => (
                        <div
                          key={singleItem.id}
                          className="overflow-hidden card-custom p-4 flex"
                        >
                          <div className="min-w-0 flex-grow">
                            <PackagedProductTraceDetailFields
                              singleItem={singleItem}
                            />
                          </div>
                          <div className="ml-4 flex shrink-0 items-start">
                            <button
                              type="button"
                              className={viewButtonClass}
                              onClick={() => openPackagingDetailsPanel(singleItem)}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-2">
                        <div className="overflow-hidden card-custom p-4">
                          <p className="text-center text-gray-500">
                            No packaged products
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </AccordionTab>
            <AccordionTab
              header="Shipment History"
              headerClassName={accordionHeaderClass}
              contentClassName={accordionContentClass}
            >
              <div className="py-2">
                {product.shippings ? (
                  product.shippings.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      {product.shippings.map((shipping) => (
                        <div
                          key={shipping.id}
                          className="overflow-hidden card-custom p-4 flex"
                        >
                          <div className="grid grid-cols-3 gap-4 flex-grow">
                            <TextWithLabel
                              label="Customer"
                              text={shipping.customer}
                            />
                            <TextWithLabel
                              label="PO #"
                              text={shipping.po_number}
                            />
                            <TextWithLabel
                              label="Order Date"
                              text={formatDate(shipping.order_date)}
                            />
                            <TextWithLabel
                              label="Quantity"
                              text={shipping.total_sold}
                            />
                            <TextWithLabel
                              label="Status"
                              text={convertStatus(shipping.status).text}
                            />
                            <TextWithLabel
                              label="Shipped date"
                              text={formatDateWithTime(shipping.shipped_date)}
                            />
                          </div>
                          <div className="ml-4 flex items-start">
                            <Link
                              className={viewButtonClass}
                              to={"/shippings/" + shipping.id}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="overflow-hidden card-custom p-4">
                      <div className="grid grid-cols-4 gap-4 mt-2">
                        <TextWithLabel label="Sale status" text="Not sold" />
                      </div>
                    </div>
                  )
                ) : null}
              </div>
            </AccordionTab>
          </Accordion>
        </div>
      </div>
    </div>
    <SlideOver
      childComponent={packagingSlideOver.childComponent}
      slideOver={packagingSlideOver}
      setSlideOver={setPackagingSlideOver}
      customClass="max-w-6xl"
    />
    </>
  );
}
